import { Alert, Box, CircularProgress, Stack, Typography } from '@mui/material';
import React from 'react';
import { FinsightEntity } from 'shared';

import { useEntityQuery } from '../../api/entity';
import EntityView from '../entityView';
import { usePinnedEntitiesController } from './index.controller';
import type { PinnedEntitiesProps } from './index.types';

export function PinnedEntitiesView(props: PinnedEntitiesProps) {
  const { pinnedEntityIds, isLoading, compact } =
    usePinnedEntitiesController(props);

  // Query entities by their IDs
  const entityQuery = useEntityQuery(
    pinnedEntityIds.length > 0
      ? [
          {
            name: 'field_query_ids',
            property: 'id',
            operator: 'in',
            value: pinnedEntityIds,
          },
        ]
      : null,
  );

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (pinnedEntityIds.length === 0) {
    return null;
  }

  if (entityQuery.isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (entityQuery.error) {
    return (
      <Alert severity="error">
        Failed to load pinned entities. Please try refreshing the page.
      </Alert>
    );
  }

  const entities = entityQuery.data || [];

  // Sort entities to match pinned order
  const sortedEntities = entities.sort(
    (a: FinsightEntity, b: FinsightEntity) => {
      const aIndex = pinnedEntityIds.indexOf(a.id);
      const bIndex = pinnedEntityIds.indexOf(b.id);
      return aIndex - bIndex;
    },
  );

  if (compact) {
    return (
      <Stack gap={1}>
        {sortedEntities.map((entity: FinsightEntity) => (
          <EntityView key={entity.id} entity={entity} viewType="list" />
        ))}
      </Stack>
    );
  }

  return (
    <Stack gap={2}>
      <Typography variant="h6">📌 Pinned Entities</Typography>
      <Stack gap={1}>
        {sortedEntities.map((entity: FinsightEntity) => (
          <EntityView key={entity.id} entity={entity} viewType="list" />
        ))}
      </Stack>
    </Stack>
  );
}
